import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Screen =
  | 'main-menu'
  | 'deck-builder'
  | 'deck-selector'
  | 'map-select'
  | 'matchmaking'
  | 'game'
  | 'match-result'
  | 'progression'
  | 'inventory'
  | 'card-detail'  // Card detail + skill tree — viewingCardId must be set before navigating
  | 'login'
  | 'register'
  | 'leaderboard'
  | 'settings'
  | 'tutorial'    // First-run interactive tutorial
  | 'feedback'    // In-game feedback & bug-report form
  | 'admin'       // Dev-only feedback admin view (requires ADMIN_API_KEY)
  | 'auth'        // Unified auth screen (login / register / forgot — tabs within one component)
  | 'mode-select'        // Choose 1v1 / 2v2 / 3v3 before queuing
  | 'campaign'           // Solo campaign mission selection
  | 'coop-matchmaking'   // Coop PvE matchmaking — 2 humans vs escalating bots
  | 'friends'            // Friends list: view, manage, challenge friends
  | 'replay'             // Match history viewer (last 20 matches)
  | 'profile'            // Player profile and avatar selection
  | 'spectator-browser'  // List of active PvP matches to watch
  | 'spectator-view'     // Live stats view of a match being spectated
  | 'tournament'         // Tournament browser, join, and bracket view
  | 'clan'               // Clan browser, create, manage, leaderboard
  | 'season-pass'        // Full-screen season pass / battle-pass screen

export type GameMode = 'pvp' | 'bot' | 'campaign' | 'coop'

/** Number of players per team in a multiplayer match. */
export type MatchFormat = '1v1' | '2v2' | '3v3'

interface NavigationState {
  currentScreen: Screen
  selectedMapId: string | null
  selectedDeckId: string | null
  editingDeckId: string | null
  gameMode: GameMode
  matchFormat: MatchFormat
  matchmakingMatchId: string | null
  /** When true, the matchmaking screen waits for MATCH_FOUND via friendsSocket (challenge match). */
  isChallengeMatch: boolean
  /** Card ID being viewed in the CardDetailScreen. Set before navigating to 'card-detail'. */
  viewingCardId: string | null
  /** Category of the card being viewed ('tower' | 'unit' | 'hero'). */
  viewingCardCategory: 'tower' | 'unit' | 'hero' | null
  /** Index of the currently selected campaign mission (0-4). */
  selectedMissionIndex: number | null
  /** MatchId of the match currently being spectated. Set before navigating to 'spectator-view'. */
  spectatingMatchId: string | null
  navigate: (screen: Screen) => void
  setSelectedMap: (mapId: string) => void
  setSelectedDeck: (deckId: string) => void
  setEditingDeckId: (id: string | null) => void
  setGameMode: (mode: GameMode) => void
  setMatchFormat: (format: MatchFormat) => void
  setMatchmakingMatchId: (id: string | null) => void
  setIsChallengeMatch: (flag: boolean) => void
  setSelectedMissionIndex: (idx: number) => void
  navigateToCard: (cardId: string, category: 'tower' | 'unit' | 'hero') => void
  setSpectatingMatchId: (id: string | null) => void
}

export const useNavigationStore = create<NavigationState>()(
  persist(
    (set) => ({
      currentScreen: 'main-menu',
      selectedMapId: null,
      selectedDeckId: null,
      editingDeckId: null,
      gameMode: 'bot',
      matchFormat: '1v1',
      matchmakingMatchId: null,
      isChallengeMatch: false,
      viewingCardId: null,
      viewingCardCategory: null,
      selectedMissionIndex: null,
      spectatingMatchId: null,
      navigate: (screen) => set({ currentScreen: screen }),
      setSelectedMap: (selectedMapId) => set({ selectedMapId }),
      setSelectedDeck: (selectedDeckId) => set({ selectedDeckId }),
      setEditingDeckId: (editingDeckId) => set({ editingDeckId }),
      setGameMode: (gameMode) => set({ gameMode }),
      setMatchFormat: (matchFormat) => set({ matchFormat }),
      setMatchmakingMatchId: (matchmakingMatchId) => set({ matchmakingMatchId }),
      setIsChallengeMatch: (isChallengeMatch) => set({ isChallengeMatch }),
      setSelectedMissionIndex: (selectedMissionIndex) => set({ selectedMissionIndex }),
      setSpectatingMatchId: (spectatingMatchId) => set({ spectatingMatchId }),
      navigateToCard: (cardId, category) => set({
        viewingCardId: cardId,
        viewingCardCategory: category,
        currentScreen: 'card-detail',
      }),
    }),
    {
      name: 'ultimaze-navigation',
      partialize: (state) => ({
        selectedMapId: state.selectedMapId,
        selectedDeckId: state.selectedDeckId,
      }),
    }
  )
)
